/**
 * CSS Variables Generation
 *
 * Generates CSS custom properties from the Color Registry.
 * Enables runtime theming and tower-specific color switching.
 *
 * @example
 * ```css
 * :root {
 *   --color-primary: #00a651;
 *   --color-secondary: #7cb342;
 * }
 *
 * .tower-vet {
 *   --color-primary: #0072CE;
 * }
 * ```
 */

import {colorRegistry, type ColorInfo} from './registry'

// ============================================================================
// TYPES
// ============================================================================

interface CSSVariableSet {
	variableName: string
	value: string
}

// ============================================================================
// CSS VARIABLE GENERATION
// ============================================================================

/**
 * Generate CSS variable name from color ID
 */
function getCSSVariableName(colorId: string): string {
	return `--color-${colorId.replace(/_/g, '-')}`
}

/**
 * Generate all CSS variables from registry
 */
function generateCSSVariables(): CSSVariableSet[] {
	return Object.values(colorRegistry).map((color: ColorInfo) => ({
		variableName: getCSSVariableName(color.id),
		value: color.hexCode,
	}))
}

/**
 * Generate CSS :root block with all color variables
 */
export function generateRootCSS(): string {
	const variables = generateCSSVariables()
	const lines = variables.map((v) => `  ${v.variableName}: ${v.value};`)
	return `:root {\n${lines.join('\n')}\n}`
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Convert hex to RGB values (for rgba usage)
 */
export function hexToRGB(
	hex: string
): {r: number; g: number; b: number} | null {
	const hexPattern =
		/^#?(?<red>[a-f\d]{2})(?<green>[a-f\d]{2})(?<blue>[a-f\d]{2})$/i
	const result = hexPattern.exec(hex)
	if (!result?.groups) return null

	const {red, green, blue} = result.groups
	if (!red || !green || !blue) return null

	return {
		r: Number.parseInt(red, 16),
		g: Number.parseInt(green, 16),
		b: Number.parseInt(blue, 16),
	}
}
